// 1:1 port of prototype/components/script/CodeView.jsx (design D3/D11): same
// name, same prop contract (prototype/components/script/CodeView.d.ts), styled
// only from token CSS vars. UI.md §4.3/client-colony "Read-only code view,
// editable via HandEdit for an active player": the Code tab renders the viewed
// version's canonical B++ source syntax-highlighted, read-only by default —
// and, when `editable`, offers a HandEdit affordance (edit toggle -> draft
// textarea -> Save/Cancel -> a diagnostics banner on a failed compile,
// preserving the draft). Promoted from the
// prototype/sketches/beekeeper-wire-and-hand-edit/code-edit.html sketch.

import type { CSSProperties } from 'react';
import { isStructuralKeyword } from './structural-keywords';

export interface CodeViewProps {
  /** B++ source text (read-only display). */
  code?: string;
  style?: CSSProperties;
  /** Whether an "Edit" toggle affordance renders at all (HandEdit, desktop-first
   * per UI.md §4.3). Omit/false to keep the original always-read-only behaviour. */
  editable?: boolean;
  /** Controlled edit-mode flag — when true, renders the draft textarea + Save/Cancel
   * instead of the read-only highlighted `<pre>`. */
  editing?: boolean;
  onToggleEdit?: () => void;
  /** The in-progress edit buffer (controlled) — preserved across a failed save so
   * `diagnostics` can render alongside the player's own text, never discarding it. */
  draft?: string;
  onDraftChange?: (value: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
  saveDisabled?: boolean;
  /** Compile diagnostics from the last failed save, if any — rendered as a danger
   * banner below the draft textarea (same visual treatment as PromptBox's failed
   * diagnostics list). */
  diagnostics?: string[];
}

// Structural keywords only (client-colony's "The highlighter SHALL match
// structural keywords only" requirement) — `isStructuralKeyword` is the single
// shared definition in `./structural-keywords`, also consumed by the
// `prototype/` kit copy. Actions, senses, self-properties and characteristics —
// the language's open vocabulary — are deliberately NOT classified here and
// render as ordinary (uncoloured) identifiers, whether or not the grammar
// recognises them.
function highlight(code: string) {
  return code.split('\n').map((line, i) => (
    <div key={i}>
      {line.split(/(\s+|->|\(|\)|:|,)/).map((tok, j) => {
        let color: string | undefined;
        if (isStructuralKeyword(tok) || tok === '->') color = '#F0B429';
        else if (/^\d+$/.test(tok)) color = '#DE9F63';
        else if (/^".*"$/.test(tok)) color = '#8FB95C';
        return (
          <span key={j} style={color ? { color } : undefined}>
            {tok}
          </span>
        );
      })}
    </div>
  ));
}

export function CodeView({
  code = '',
  style,
  editable = false,
  editing = false,
  onToggleEdit,
  draft = '',
  onDraftChange,
  onSave,
  onCancel,
  saveDisabled = false,
  diagnostics,
}: CodeViewProps) {
  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...style }}>
        <textarea
          data-testid="code-edit-draft"
          value={draft}
          onChange={(e) => onDraftChange?.(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            minHeight: 180,
            resize: 'vertical',
            background: 'var(--hud-bg-solid)',
            color: '#F6ECD6',
            border: `1.5px solid ${diagnostics && diagnostics.length > 0 ? 'var(--danger)' : 'var(--accent)'}`,
            borderRadius: 'var(--r-md)',
            padding: 'var(--sp-4)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            lineHeight: 1.65,
          }}
        />
        {diagnostics && diagnostics.length > 0 && (
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--danger)',
              background: 'var(--danger-soft)',
              borderRadius: 'var(--r-md)',
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {diagnostics.map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            data-testid="code-edit-cancel"
            onClick={onCancel}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 500,
              fontSize: 13,
              minHeight: 40,
              padding: '0 16px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--surface-card)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line-strong)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="code-edit-save"
            onClick={onSave}
            disabled={saveDisabled}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 500,
              fontSize: 13,
              minHeight: 40,
              padding: '0 20px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--accent-fill)',
              color: '#FFFDF6',
              border: '1px solid var(--accent-strong)',
              cursor: saveDisabled ? 'default' : 'pointer',
              opacity: saveDisabled ? 0.5 : 1,
            }}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {editable && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            data-testid="code-edit-toggle"
            onClick={onToggleEdit}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 12,
              minHeight: 32,
              padding: '0 12px',
              borderRadius: 'var(--r-pill)',
              border: '1px solid var(--line-strong)',
              background: 'var(--surface-card)',
              color: 'var(--accent-strong)',
              cursor: 'pointer',
            }}
          >
            ✎ Edit
          </button>
        </div>
      )}
      {/* **UI/UX audit F-04.** This was `white-space: pre` (the `<pre>` default) with `overflow-x:
          auto`, which at 375px clipped every long B++ line: `flee_home` — the *target* of a
          transition — rendered 74px past the box and 45px past the viewport. It was technically
          reachable, but only by a horizontal swipe inside a panel that also scrolls vertically, with
          no scrollbar (mobile hides them) and no reserved scrollbar space, so nothing indicated
          anything was missing.

          Soft-wrap instead of scrolling: B++ lines are short by language design (BPP-SPEC §9 limits),
          so wrapping is rare, and the Flowchart tab is the wide-format view when structure matters
          more than bytes. `pre-wrap` keeps the leading indentation that carries the block structure,
          and the hanging indent (`text-indent` negative against a matching `padding-left`) makes a
          wrapped continuation visually subordinate to its own line rather than looking like a new
          statement. `overflow-wrap: anywhere` is the backstop for a single token longer than the box —
          an identifier can be 24 chars (BPP-SPEC §9) and must not reintroduce the clip. */}
      <pre
        style={{
          margin: 0,
          background: 'var(--hud-bg-solid)',
          color: '#F6ECD6',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          paddingLeft: 'calc(var(--sp-4) + 2ch)',
          textIndent: '-2ch',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          lineHeight: 1.65,
          ...style,
        }}
      >
        {highlight(code)}
      </pre>
    </div>
  );
}
